import { useOptionalAppAssistant } from '@/lib/app-assistant-context';
import { Button } from '@/components/ui/Button';
import { useId, useState, type ReactNode } from 'react';
import type { WorkActionPresentation } from '@shared/types/work-action';
import { ObjectiveContribution } from '@/components/objectives/ObjectiveContribution';
import {
  businessSupportingText,
  sweepBusinessVocabulary,
  sweepSerializedBusinessText,
} from '@/lib/ui-editorial-sweeper';
import { WorkActionControl } from './WorkActionControl';
import { ChevronDown } from 'lucide-react';

export const WorkActionCard = ({
  work,
  compact = false,
  collapseContext = false,
  children,
  controls,
  headingAction,
  headingId,
  hideControls = false,
  showAdjustment = true,
  layout = 'stack',
  leading,
  subline,
}: {
  work: WorkActionPresentation;
  compact?: boolean;
  collapseContext?: boolean;
  children?: ReactNode;
  controls?: ReactNode;
  headingAction?: ReactNode;
  headingId?: string;
  hideControls?: boolean;
  showAdjustment?: boolean;
  layout?: 'stack' | 'row';
  leading?: ReactNode;
  /** Row layout: one line under the title (what was last done on this task). */
  subline?: ReactNode;
}) => {
  const [contextOpen, setContextOpen] = useState(false);
  const contextId = useId();
  const assistant = useOptionalAppAssistant();
  const title = sweepBusinessVocabulary(sweepSerializedBusinessText(work.title));
  const supportingReason = businessSupportingText(work.reason, title);
  const reason = supportingReason ? sweepBusinessVocabulary(supportingReason) : null;
  const Heading = compact ? 'h3' : 'h2';
  const hasContext = Boolean(
    reason || work.dueAt || work.notice || work.evidenceRefs.length > 0
  );
  const context = (
    <div className={collapseContext ? undefined : 'mt-2'}>
      {reason && (
        <p className="max-w-3xl text-sm leading-6 text-domino-inkSoft">{reason}</p>
      )}
      {work.dueAt && (
        <p className="mt-2 text-xs text-domino-inkSoft">
          Échéance métier :{' '}
          {new Date(work.dueAt).toLocaleDateString('fr-FR', {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
          })}
        </p>
      )}
      <ObjectiveContribution evidenceRefs={work.evidenceRefs} />
      {work.notice && (
        <p
          role="status"
          className="mt-3 border-l-2 border-domino-coral pl-3 text-sm leading-6 text-domino-inkSoft"
        >
          {sweepBusinessVocabulary(work.notice)}
        </p>
      )}
    </div>
  );
  if (layout === 'row') {
    return (
      <div
        data-work-action={work.id}
        className="work-action-row"
      >
        <div className="work-account min-w-0">{leading}</div>
        <div className="work-menu">{headingAction}</div>
        <div className="work-title flex min-w-0 items-center gap-2">
          <div className="min-w-0 flex-1">
            <Heading
              id={headingId}
              title={title}
              className={`min-w-0 text-sm font-normal leading-5 text-domino-ink ${contextOpen ? '' : 'line-clamp-2'}`}
            >
              {title}
            </Heading>
            {subline}
          </div>
          {(hasContext || title.length > 80) && <button
            type="button"
            aria-expanded={contextOpen}
            aria-controls={contextId}
            onClick={() => setContextOpen((open) => !open)}
            title="Voir le contexte"
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded text-domino-inkSoft hover:bg-domino-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-domino-prune"
          >
            <span className="sr-only">Voir le contexte</span>
            <ChevronDown className={`h-4 w-4 transition ${contextOpen ? 'rotate-180' : ''}`} />
          </button>}
        </div>
        <div className="work-command min-w-0">
          {controls ?? <WorkActionControl work={work} showAdjustment={false} dense />}
        </div>
        {contextOpen && (
          <div id={contextId} className="col-span-full border-t border-domino-cardBorder py-3">
            {context}
          </div>
        )}
      </div>
    );
  }
  return (
    <div data-work-action={work.id} className="min-w-0">
      <div className="flex min-w-0 items-start justify-between gap-3">
        <Heading
          id={headingId}
          className={
            compact
              ? 'min-w-0 text-base font-semibold leading-6 text-domino-ink'
              : 'min-w-0 text-xl font-semibold leading-8 tracking-tight text-domino-ink sm:text-2xl'
          }
        >
          {title}
        </Heading>
        {headingAction}
      </div>
      {!collapseContext && context}
      {!hideControls && <div
        className={
          collapseContext
            ? 'mt-3 flex flex-wrap items-center gap-3'
            : 'mt-4 flex flex-wrap items-center gap-3'
        }
      >
        {controls ?? <WorkActionControl work={work} showAdjustment={showAdjustment} />}
        {children}
        {showAdjustment &&
          assistant &&
          work.command.kind !== 'work' &&
          work.command.kind !== 'prepare_plan' && (
            <Button variant="ghost" onClick={() => assistant.openWork(work.context)}>
              Ajuster la demande
            </Button>
          )}
      </div>}
      {collapseContext && hasContext && (
        <details className="group mt-2">
          <summary className="inline-flex cursor-pointer list-none items-center gap-1 py-1 text-xs font-medium text-domino-inkSoft hover:text-domino-aubergine focus:outline-none focus-visible:ring-2 focus-visible:ring-domino-prune">
            Voir le contexte
            <ChevronDown className="h-3.5 w-3.5 transition group-open:rotate-180" />
          </summary>
          <div className="mt-2 max-w-3xl border-l border-domino-cardBorder pl-3">
            {context}
          </div>
        </details>
      )}
    </div>
  );
};
